import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LogOut,
  Search,
} from "lucide-react";
import { useAuth, isAdmin } from "@/state/AuthContext";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/form";

const navItems = [
  { to: "/", end: true, label: "Dashboard", letter: "D" },
  { to: "/admin/customers", label: "Customers", letter: "C", admin: true },
  { to: "/admin/sellers", label: "Sellers", letter: "S", admin: true },
  { to: "/admin/users", label: "Users", letter: "U", admin: true },
  { to: "/admin/custom-fields", label: "Custom Fields", letter: "F", admin: true },
];

function NavItem({
  to,
  end,
  label,
  letter,
}: {
  to: string;
  end?: boolean;
  label: string;
  letter: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
          isActive
            ? "bg-argon-primary/10 text-argon-primary"
            : "text-argon-dark hover:bg-gray-50",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold shadow-sm",
              isActive ? "bg-argon-primary text-white" : "bg-white text-argon-secondary border border-gray-100",
            )}
          >
            {letter}
          </span>
          {label}
        </>
      )}
    </NavLink>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  const pageTitle =
    location.pathname === "/"
      ? "Dashboard"
      : location.pathname.startsWith("/rfqs/new")
        ? "New RFQ"
        : location.pathname.startsWith("/rfqs/")
          ? "RFQ Detail"
          : location.pathname.includes("customers")
            ? "Customers"
            : location.pathname.includes("sellers")
              ? "Sellers"
              : location.pathname.includes("users")
                ? "Users"
                : location.pathname.includes("custom-fields")
                  ? "Custom Fields"
                  : "MWL Lumber";

  return (
    <div className="min-h-svh flex bg-argon-body">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-white shadow-[0_0_2rem_0_rgba(136,152,170,0.15)] sticky top-0 h-svh">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-argon-primary text-white font-bold text-sm">
              M
            </div>
            <div>
              <div className="text-sm font-bold text-argon-dark leading-tight">MWL Lumber</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-argon-secondary">
            Pages
          </p>
          <NavItem to="/" end label="Dashboard" letter="D" />
          {isAdmin(user) && (
            <>
              <p className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-argon-secondary">
                Admin
              </p>
              <NavItem to="/admin/customers" label="Customers" letter="C" />
              <NavItem to="/admin/sellers" label="Sellers" letter="S" />
              <NavItem to="/admin/users" label="Users" letter="U" />
              <NavItem to="/admin/custom-fields" label="Custom Fields" letter="F" />
            </>
          )}
        </nav>

        <div className="p-4 m-3 rounded-2xl bg-gradient-to-br from-argon-primary to-[#825ee4] text-white">
          <p className="text-xs font-semibold opacity-90">Signed in as</p>
          <p className="text-sm font-bold truncate mt-0.5">{user?.displayName}</p>
          <p className="text-xs capitalize opacity-80">{user?.role}</p>
          <button
            type="button"
            onClick={logout}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold underline underline-offset-2 hover:opacity-90"
          >
            <LogOut className="h-3.5 w-3.5" />
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-gray-100 px-4 md:px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="md:hidden flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-argon-primary text-white font-bold text-xs">
                M
              </div>
              <span className="font-bold text-argon-dark text-sm">{pageTitle}</span>
            </div>
            <nav className="hidden md:flex items-center text-sm text-argon-secondary">
              <span>Pages</span>
              <span className="mx-2">/</span>
              <span className="text-argon-dark font-semibold">{pageTitle}</span>
            </nav>
            <div className="flex items-center gap-3 ml-auto">
              <div className="relative hidden sm:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-argon-secondary" />
                <Input placeholder="Type here..." className="pl-9 w-48 lg:w-64 h-9 bg-gray-50 border-0" />
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-argon-primary/10 text-argon-primary text-sm font-bold">
                {user?.displayName?.charAt(0)?.toUpperCase() ?? "U"}
              </div>
            </div>
          </div>

          <nav className="md:hidden flex gap-2 mt-3 overflow-x-auto pb-1 text-xs">
            {navItems
              .filter((item) => !item.admin || isAdmin(user))
              .map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      "whitespace-nowrap rounded-full px-3 py-1 font-semibold",
                      isActive ? "bg-argon-primary text-white" : "bg-gray-100 text-argon-secondary",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
          </nav>
        </header>

        <main className="flex-1 px-4 md:px-6 pb-8">
          <Outlet />
        </main>

        <footer className="px-6 py-4 border-t border-gray-100 text-center text-sm text-argon-secondary">
          © {new Date().getFullYear()} MWL Lumber
        </footer>
      </div>
    </div>
  );
}
